import React, { useState } from 'react'
import { Plus, Edit2, Trash2, Key, DollarSign } from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../../services/adminApi'
import StatusBadge from '../../../components/admin/StatusBadge'
import ConfirmDialog from '../../../components/admin/ConfirmDialog'
import AccountEditModal from './AccountEditModal'

/**
 * AccountsList - Display and manage accounts within a provider configuration
 */
const AccountsList = ({ configId, accounts, onAccountsChange, addToast }) => {
  const [showAccountModal, setShowAccountModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, account: null })
  const [isDeleting, setIsDeleting] = useState(false)

  // Open create account modal
  const handleAddAccount = () => {
    setEditingAccount(null)
    setShowAccountModal(true)
  }

  // Open edit account modal
  const handleEditAccount = (account) => {
    setEditingAccount(account)
    setShowAccountModal(true)
  }

  // Handle account form submit
  const handleAccountSubmit = async (data) => {
    try {
      if (editingAccount) {
        await adminApi.updateAccount(editingAccount.id, data)
        addToast('Account updated successfully', 'success')
      } else {
        await adminApi.createAccount(data)
        addToast('Account created successfully', 'success')
      }
      setShowAccountModal(false)
      setEditingAccount(null)
      onAccountsChange()
    } catch (err) {
      console.error('Failed to save account:', err)
      addToast(err.response?.data?.detail || 'Failed to save account', 'error')
      throw err
    }
  }

  // Confirm delete account
  const handleDeleteClick = (account) => {
    setDeleteConfirm({ open: true, account })
  }

  // Execute delete account
  const handleDeleteConfirm = async () => {
    const account = deleteConfirm.account
    if (!account) return

    try {
      setIsDeleting(true)
      await adminApi.deleteAccount(account.id)
      addToast('Account deleted successfully', 'success')
      setDeleteConfirm({ open: false, account: null })
      onAccountsChange()
    } catch (err) {
      console.error('Failed to delete account:', err)
      addToast(err.response?.data?.detail || 'Failed to delete account', 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  // Format price for display (price per million tokens)
  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-'
    return `$${price.toFixed(2)}`
  }

  // Mask API key
  const maskApiKey = (key) => {
    if (!key) return '-'
    if (key.length <= 8) return '••••••••'
    return '••••' + key.slice(-4)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Model Accounts ({accounts.length})
        </h4>
        <button
          onClick={handleAddAccount}
          className={clsx(
            'inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md',
            'text-primary-700 dark:text-primary-300',
            'bg-primary-50 dark:bg-primary-900/30',
            'hover:bg-primary-100 dark:hover:bg-primary-900/50',
            'border border-primary-200 dark:border-primary-800'
          )}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add Account
        </button>
      </div>

      {/* Accounts Table */}
      {accounts.length === 0 ? (
        <div className="text-center py-6 bg-gray-100 dark:bg-gray-900/50 rounded-lg">
          <Key className="w-6 h-6 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No accounts configured for this provider
          </p>
          <button
            onClick={handleAddAccount}
            className="mt-2 text-sm text-primary-600 hover:text-primary-500"
          >
            Add your first account
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr className="text-xs text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2 text-left font-medium">Model</th>
                <th className="px-3 py-2 text-left font-medium">Endpoint</th>
                <th className="px-3 py-2 text-left font-medium">API Key</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">
                  <div className="flex items-center">
                    <DollarSign className="w-3 h-3 mr-1" />
                    $/M tokens (In/Out)
                  </div>
                </th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2">
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300">
                      {account.model}
                    </code>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px] block" title={account.api_endpoint}>
                      {account.api_endpoint?.replace('https://', '').split('/')[0] || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                      {maskApiKey(account.api_key)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={account.is_active ? 'active' : 'inactive'} size="sm" />
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      {formatPrice(account.price_pmt_in)} / {formatPrice(account.price_pmt_out)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <button
                        onClick={() => handleEditAccount(account)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Edit account"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(account)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete account"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Account Edit Modal */}
      {showAccountModal && (
        <AccountEditModal
          account={editingAccount}
          configId={configId}
          onSubmit={handleAccountSubmit}
          onClose={() => {
            setShowAccountModal(false)
            setEditingAccount(null)
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, account: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Account"
        message={`Are you sure you want to delete "${deleteConfirm.account?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

export default AccountsList
